import { useTranslation } from 'react-i18next';

import { ColumnProps } from 'antd/lib/table';
import { Dropdown, Button, Menu } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { DateFormatted } from 'Components';
import {
	useCreateColumns,
	ColumnFilters,
	UseCreateColumnsInterface
} from 'Components/Table/createColumns';
import { getAppRoutes } from 'Pages/App/App.constants';
import { GetSystemRolesRequest, SystemRole } from 'Services/Api/Roles/Types';
import { GetUsersRequest } from 'Services/Api/Users/Types';
import { qsStringify, toPath } from 'Helpers/navigation';
import { getUserRoleLabel } from 'app-util';
import { generatePath, Link } from 'react-router-dom';
import './styles/Roles.module.scss';
type FilterValues = GetSystemRolesRequest['filters'];
export type TableFilterValues = ColumnFilters<FilterValues>;

export interface RoleRowActions<T extends SystemRole> {
	onViewDetail?: (record: T) => void;
	onEdit?: (record: T) => void;
	onRestoreToDefaults?: (record: T) => void;
	onClone?: (record: T) => void;
	onDelete?: (record: T) => void;
}

export function useColumnProps<T extends SystemRole>(
	helpers?: ReturnType<UseCreateColumnsInterface<T, FilterValues>>,
	actions?: RoleRowActions<T>
) {
	const { t } = useTranslation();

	const columns: Array<ColumnProps<T>> = [
		{
			...helpers?.sort,
			sortOrder: helpers?.getSortOrder('roleName'),
			title: t('roles.columns.role'),
			key: 'roleName',
			dataIndex: 'roleName',
			defaultSortOrder: 'ascend',
			sorter: true,
			width: 144,
			filteredValue: helpers?.filters.roleName,
			render: (_, record) => (
				<Link
					to={generatePath(getAppRoutes().EDIT_ROLE, {
						roleId: record.roleName
					})}
					onClick={() => actions?.onEdit?.(record)}
				>
					{getUserRoleLabel(record.roleName)}
				</Link>
			)
		},
		{
			...helpers?.sort,
			sortOrder: helpers?.getSortOrder('description'),
			title: t('roles.columns.description'),
			key: 'description',
			defaultSortOrder: 'ascend',
			sorter: true,
			dataIndex: 'description',
			width: 434,
		},
		{
			...helpers?.sort,
			sorter: true,
			sortOrder: helpers?.getSortOrder('type'),
			title: t('roles.columns.type'),
			key: 'type',
			dataIndex: 'type',
			defaultSortOrder: 'ascend',
			width: 150,
			render: (_, record) => (
				<span className={record.type === 'Primary' ? 'primary-role-batch' : 'secondary-role-batch'}>{record.type}</span>
			)
		},
		{
			...helpers?.sort,
			sorter: true,
			sortOrder: helpers?.getSortOrder('usersCount'),
			title: t('roles.columns.usersCount'),
			key: 'usersCount',
			defaultSortOrder: 'ascend',
			dataIndex: 'usersCount',
			width: 150,
			render: (value, record) => {
				if (value === 0) {
					return value;
				}
				const usersFilters: GetUsersRequest['filters'] = {
					systemRoles: [record.roleName]
				};
				const to = toPath({
					path: getAppRoutes().USERS,
					query: qsStringify(usersFilters)
				});
				return <Link to={to}>{value}</Link>;
			}
		},
		{
			...helpers?.sort,
			sorter: true,
			sortOrder: helpers?.getSortOrder('updatedDate'),
			title: t('roles.columns.lastUpdate'),
			key: 'updatedDate',
			dataIndex: 'updatedDate',
			width: 287,
			render: (value) => !!value ? <DateFormatted date={value} /> : ""
		},
		{
			key: 'actions',
			dataIndex: 'actions',
			width: 36,
			render: (_, record) => {
				const menu = (
					<Menu className="or-list-selector">
						<Menu.Item
							key="viewDetail"
							onClick={() => actions?.onViewDetail?.(record)}
						>
							{t('roles.actions.view')}
						</Menu.Item>

						<Menu.Item
							key="edit"
							onClick={() => actions?.onEdit?.(record)}
						>
							{t('roles.actions.edit')}
						</Menu.Item>
						{record.type?.toLowerCase() === 'primary' && !!record?.isPrimaryRoleOverride &&
							<Menu.Item
								key="restoreToDefaults"
								onClick={() => actions?.onRestoreToDefaults?.(record)}
							>
								{t('roles.actions.restoreToDefaults')}
							</Menu.Item>
						}
						<Menu.Item
							key="clone"
							onClick={() => actions?.onClone?.(record)}
						>
							{t('roles.actions.clone')}
						</Menu.Item>
						{record.type !== 'Primary' &&
							<Menu.Item
								key="delete"
								onClick={() => actions?.onDelete?.(record)}
							>
								<span className='role-action-delete'>{t('roles.actions.delete')}</span>
							</Menu.Item>
						}
					</Menu>
				);

				return (
					<Dropdown
						overlay={menu}
						trigger={['click']}
						placement="bottomRight"
					>
						<Button
							type="text"
							icon={<MoreOutlined />}
							onClick={(e) => e.stopPropagation()}
						/>
					</Dropdown>
				);
			}
		}
	];
	return columns;
}

const useColumns = <T extends SystemRole>({
	params,
	actions
}: {
	params: GetSystemRolesRequest | undefined;
	actions?: RoleRowActions<T>;
}) => {
	const helpers = useCreateColumns<T, FilterValues>({
		filterValues: params?.filters || {},
		sortDesc: params?.sort
	});
	return useColumnProps(helpers, actions);
};

export default useColumns;